import React, { useEffect, useMemo, useState, useRef } from "react";
import axios from "../../utils/axiosInstance";

/* ============================================================
   CONSTANTS
============================================================ */

const AFRICAN_COUNTRIES = [
  "CM",
  "CF",
  "TD",
  "CG",
  "GA",
  "GQ",
  "BJ",
  "BF",
  "CI",
  "GW",
  "ML",
  "NE",
  "SN",
  "TG",
  "CD",
  "NG",
  "GH",
];

const MOBILE_OPERATORS_BY_COUNTRY = {
  CM: ["ORANGE_MONEY", "MTN_MOMO"],
  CF: ["ORANGE_MONEY", "TELECEL", "MOOV_AFRICA"],
  TD: ["AIRTEL_MONEY", "MOOV_AFRICA"],
  CG: ["AIRTEL_MONEY", "MTN_CONGO", "AZUR_CONGO", "CONGO_TELECOM"],
  GA: ["AIRTEL_MONEY", "MOOV_AFRICA"],
  GQ: ["ORANGE_MONEY", "MUNI"],

  BJ: ["MTN_MOMO", "MOOV_AFRICA"],
  BF: ["ORANGE_MONEY", "MOOV_AFRICA", "WAVE"],
  CI: ["ORANGE_MONEY", "MTN_MOMO", "WAVE"],
  GW: ["ORANGE_MONEY", "MTN_MOMO"],
  ML: ["ORANGE_MONEY", "MOOV_AFRICA", "WAVE"],
  NE: ["ORANGE_MONEY", "WAVE", "AIRTEL_MONEY", "MOOV_AFRICA"],
  SN: ["ORANGE_MONEY", "WAVE"],
  TG: ["WAVE", "TOGOCOM", "MOOV_AFRICA"],

  CD: ["M_PESA", "AIRTEL_MONEY", "AFRICELL"],
  GH: ["MTN_GHANA", "TELECEL_GHANA", "AIRTELTIGO_GHANA"],
};

const DESTINATION_METHODS = {
  CA: ["INTERAC"],

  CM: ["MOBILE_MONEY", "BANK_TRANSFER"],
  CF: ["MOBILE_MONEY", "BANK_TRANSFER"],
  TD: ["MOBILE_MONEY", "BANK_TRANSFER"],
  CG: ["MOBILE_MONEY", "BANK_TRANSFER"],
  GA: ["MOBILE_MONEY", "BANK_TRANSFER"],
  GQ: ["MOBILE_MONEY", "BANK_TRANSFER"],

  BJ: ["MOBILE_MONEY", "BANK_TRANSFER"],
  BF: ["MOBILE_MONEY", "BANK_TRANSFER"],
  CI: ["MOBILE_MONEY", "BANK_TRANSFER"],
  GW: ["MOBILE_MONEY", "BANK_TRANSFER"],
  ML: ["MOBILE_MONEY", "BANK_TRANSFER"],
  NE: ["MOBILE_MONEY", "BANK_TRANSFER"],
  SN: ["MOBILE_MONEY", "BANK_TRANSFER"],
  TG: ["MOBILE_MONEY", "BANK_TRANSFER"],

  CD: ["MOBILE_MONEY", "BANK_TRANSFER"],
  NG: ["BANK_TRANSFER"],
  GH: ["MOBILE_MONEY", "BANK_TRANSFER"],
};

const isCanada = (c) => String(c || "").toUpperCase() === "CA";
const isAfrica = (c) =>
  AFRICAN_COUNTRIES.includes(String(c || "").toUpperCase());

const isValidEmail = (email) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || "").trim());

function setIfEmpty(current, fallback) {
  if (current === undefined || current === null) return fallback ?? "";
  if (typeof current === "string" && current.trim() === "")
    return fallback ?? "";
  return current;
}

function isObjEmptyForDraft(o = {}) {
  return (
    !String(o.firstName || "").trim() &&
    !String(o.lastName || "").trim() &&
    !String(o.email || "").trim() &&
    !String(o.phone || "").trim()
  );
}

function mergeOnlyEmptyFields(target = {}, source = {}) {
  const out = { ...target };
  Object.keys(source || {}).forEach((k) => {
    const cur = out[k];
    const incoming = source[k];

    const curEmpty =
      cur === undefined ||
      cur === null ||
      (typeof cur === "string" && cur.trim() === "");

    if (curEmpty && incoming !== undefined && incoming !== null) {
      out[k] = incoming;
    }
  });
  return out;
}

/* ============================================================
   COMPONENT
============================================================ */

export default function SenderAndBeneficiaryStep({
  formData,
  setFormData,
  currentUser,
  onNext,
  onPrev,
}) {
  const { method, countryFrom, countryTo } = formData;

  const [beneficiaries, setBeneficiaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [touched, setTouched] = useState(false);

  const restoredRef = useRef(false);

  const draftKey = useMemo(() => {
    const uid = currentUser?.id || currentUser?._id || "guest";
    const from = String(countryFrom || "NA").toUpperCase();
    const to = String(countryTo || "NA").toUpperCase();
    const m = String(method || "NA").toUpperCase();
    return `transfer_sender_beneficiary:${uid}:${from}:${to}:${m}`;
  }, [currentUser?.id, currentUser?._id, countryFrom, countryTo, method]);

  useEffect(() => {
    if (restoredRef.current) return;
    restoredRef.current = true;

    const saved = localStorage.getItem(draftKey);
    if (!saved) return;

    try {
      const parsed = JSON.parse(saved);

      setFormData((prev) => {
        const prevSender = prev.sender || {};
        const prevBen = prev.beneficiary || {};

        const senderLooksEmpty = isObjEmptyForDraft(prevSender);
        const benLooksEmpty = isObjEmptyForDraft(prevBen);

        return {
          ...prev,
          sender: senderLooksEmpty
            ? mergeOnlyEmptyFields(prevSender, parsed?.sender || {})
            : prevSender,
          beneficiary: benLooksEmpty
            ? mergeOnlyEmptyFields(prevBen, parsed?.beneficiary || {})
            : prevBen,
        };
      });
    } catch (_) {}
  }, [draftKey, setFormData]);

  useEffect(() => {
    try {
      localStorage.setItem(
        draftKey,
        JSON.stringify({
          sender: formData.sender,
          beneficiary: formData.beneficiary,
        }),
      );
    } catch (_) {}
  }, [draftKey, formData.sender, formData.beneficiary]);

  useEffect(() => {
    if (!currentUser) return;

    setFormData((prev) => {
      const prevSender = prev.sender || {};
      const profile = currentUser.profile || currentUser.UserProfile || {};

      const fn = setIfEmpty(prevSender.firstName, currentUser.firstName);
      const ln = setIfEmpty(prevSender.lastName, currentUser.lastName);

      return {
        ...prev,
        sender: {
          ...prevSender,
          firstName: fn,
          lastName: ln,
          fullName: setIfEmpty(
            prevSender.fullName,
            `${fn || ""} ${ln || ""}`.trim(),
          ),
          email: setIfEmpty(prevSender.email, currentUser.email),
          phone: setIfEmpty(
            prevSender.phone,
            currentUser.phoneNumber || profile.phone,
          ),
          country: setIfEmpty(
            prevSender.country,
            profile.countryCode || countryFrom,
          ),
          streetAddress: setIfEmpty(
            prevSender.streetAddress,
            profile.address || profile.streetAddress,
          ),
          city: setIfEmpty(prevSender.city, profile.city),
          province: setIfEmpty(prevSender.province, profile.province),
          postalCode: setIfEmpty(prevSender.postalCode, profile.postalCode),
        },
      };
    });
  }, [currentUser, setFormData, countryFrom]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get("/api/beneficiaries/my");
        setBeneficiaries(res.data?.beneficiaries || res.data || []);
      } catch {
        setBeneficiaries([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const allowed =
      DESTINATION_METHODS[String(countryTo || "").toUpperCase()] || [];
    if (allowed.length && !allowed.includes(method)) {
      setFormData((p) => ({
        ...p,
        method: allowed[0],
        beneficiary: {
          ...p.beneficiary,
          operator: "",
          interacEmail: "",
          bankName: "",
          accountNumber: "",
          phone:
            allowed[0] === "MOBILE_MONEY"
              ? p.beneficiary?.phone || ""
              : p.beneficiary?.phone,
        },
      }));
    }
  }, [countryTo, method, setFormData]);

  const senderIsCA = isCanada(countryFrom);
  const destIsCA = isCanada(countryTo);
  const destIsAfrica = isAfrica(countryTo);

  const operators =
    method === "MOBILE_MONEY"
      ? MOBILE_OPERATORS_BY_COUNTRY[String(countryTo || "").toUpperCase()] || []
      : [];

  const errors = useMemo(() => {
    const e = {};
    const s = formData.sender || {};
    const b = formData.beneficiary || {};

    if (!s.firstName?.trim()) e.senderFirstName = "Prénom requis";
    if (!s.lastName?.trim()) e.senderLastName = "Nom requis";
    if (!s.phone?.trim()) e.senderPhone = "Téléphone requis";
    if (!s.email?.trim()) e.senderEmail = "Email requis";
    else if (!isValidEmail(s.email)) e.senderEmail = "Email invalide";

    if (senderIsCA) {
      if (!s.streetAddress?.trim()) e.senderStreet = "Adresse requise";
      if (!s.city?.trim()) e.senderCity = "Ville requise";
      if (!s.province?.trim()) e.senderProvince = "Province requise";
      if (!s.postalCode?.trim()) e.senderPostal = "Code postal requis";
    } else {
      if (!s.city?.trim()) e.senderCity = "Ville requise";
    }

    if (!b.firstName?.trim()) e.beneficiaryFirstName = "Prénom requis";
    if (!b.lastName?.trim()) e.beneficiaryLastName = "Nom requis";

    if (method === "MOBILE_MONEY") {
      if (!b.operator?.trim()) e.beneficiaryOperator = "Opérateur requis";
      if (!b.phone?.trim()) e.beneficiaryPhone = "Téléphone requis";
    }

    if (method === "BANK_TRANSFER") {
      if (!b.bankName?.trim()) e.beneficiaryBank = "Bank Name requis";
      if (!b.accountNumber?.trim()) {
        e.beneficiaryAccount =
          String(countryTo || "").toUpperCase() === "NG"
            ? "Nigerian Account Number requis"
            : "Compte requis";
      }
    }

    if (method === "INTERAC") {
      if (!b.interacEmail?.trim()) e.beneficiaryInterac = "Email requis";
      else if (!isValidEmail(b.interacEmail))
        e.beneficiaryInterac = "Email invalide";
    }

    if (method !== "MOBILE_MONEY" && !destIsCA) {
      if (!b.email?.trim() && method !== "BANK_TRANSFER") {
        e.beneficiaryEmail = "Email requis";
      } else if (b.email?.trim() && !isValidEmail(b.email)) {
        e.beneficiaryEmail = "Email invalide";
      }
    }

    if (destIsCA) {
      if (!b.streetAddress?.trim()) e.beneficiaryStreet = "Adresse requise";
      if (!b.city?.trim()) e.beneficiaryCity = "Ville requise";
      if (!b.province?.trim()) e.beneficiaryProvince = "Province requise";
      if (!b.postalCode?.trim()) e.beneficiaryPostal = "Code postal requis";
    } else if (destIsAfrica) {
      if (!b.city?.trim() && method !== "BANK_TRANSFER")
        e.beneficiaryCity = "Ville requise";
    }

    return e;
  }, [formData, method, senderIsCA, destIsCA, destIsAfrica, countryTo]);

  const canContinue = Object.keys(errors).length === 0;
  const showError = (key) => touched && errors[key];

  const selectBeneficiary = (id) => {
    const b = beneficiaries.find((x) => String(x.id) === String(id));
    if (!b) return;

    setFormData((prev) => ({
      ...prev,
      beneficiary: {
        ...prev.beneficiary,
        id: b.id,
        firstName: b.firstName || "",
        lastName: b.lastName || "",
        fullName:
          `${b.firstName || ""} ${b.lastName || ""}`.trim() || b.fullName || "",
        phone: b.phone || "",
        email: b.email || "",
        operator: b.mobileOperator || "",
        bankName: b.bankName || "",
        accountNumber: b.accountNumber || "",
        interacEmail: b.interacEmail || "",
        streetAddress: b.streetAddress || "",
        city: b.city || "",
        province: b.province || "",
        postalCode: b.postalCode || "",
      },
    }));
  };

  if (loading) return <p>Chargement…</p>;

  return (
    <div className="space-y-6">
      <Section title="Expéditeur">
        <Row>
          <Input
            label="Prénom"
            required
            value={formData.sender?.firstName}
            error={showError("senderFirstName")}
            onChange={(v) =>
              setFormData((p) => ({
                ...p,
                sender: {
                  ...p.sender,
                  firstName: v,
                  fullName: `${v || ""} ${p.sender?.lastName || ""}`.trim(),
                },
              }))
            }
          />
          <Input
            label="Nom"
            required
            value={formData.sender?.lastName}
            error={showError("senderLastName")}
            onChange={(v) =>
              setFormData((p) => ({
                ...p,
                sender: {
                  ...p.sender,
                  lastName: v,
                  fullName: `${p.sender?.firstName || ""} ${v || ""}`.trim(),
                },
              }))
            }
          />
        </Row>

        <Input
          label="Email"
          required
          value={formData.sender?.email}
          error={showError("senderEmail")}
          onChange={(v) =>
            setFormData((p) => ({ ...p, sender: { ...p.sender, email: v } }))
          }
        />

        <Row>
          <Input
            label="Téléphone"
            required
            value={formData.sender?.phone}
            error={showError("senderPhone")}
            onChange={(v) =>
              setFormData((p) => ({ ...p, sender: { ...p.sender, phone: v } }))
            }
          />
        </Row>

        {senderIsCA ? (
          <>
            <Input
              label="Adresse (numéro et rue)"
              required
              value={formData.sender?.streetAddress}
              error={showError("senderStreet")}
              onChange={(v) =>
                setFormData((p) => ({
                  ...p,
                  sender: { ...p.sender, streetAddress: v },
                }))
              }
            />
            <Row>
              <Input
                label="Ville"
                required
                value={formData.sender?.city}
                error={showError("senderCity")}
                onChange={(v) =>
                  setFormData((p) => ({
                    ...p,
                    sender: { ...p.sender, city: v },
                  }))
                }
              />
              <Input
                label="Province"
                required
                value={formData.sender?.province}
                error={showError("senderProvince")}
                onChange={(v) =>
                  setFormData((p) => ({
                    ...p,
                    sender: { ...p.sender, province: v },
                  }))
                }
              />
            </Row>
            <Input
              label="Code postal"
              required
              value={formData.sender?.postalCode}
              error={showError("senderPostal")}
              onChange={(v) =>
                setFormData((p) => ({
                  ...p,
                  sender: { ...p.sender, postalCode: v },
                }))
              }
            />
          </>
        ) : (
          <Input
            label="Ville"
            required
            value={formData.sender?.city}
            error={showError("senderCity")}
            onChange={(v) =>
              setFormData((p) => ({ ...p, sender: { ...p.sender, city: v } }))
            }
          />
        )}
      </Section>

      <Section title="Bénéficiaire">
        {beneficiaries.length > 0 && (
          <div className="w-full">
            <label className="text-sm mb-1 block">
              Bénéficiaire enregistré
            </label>
            <select
              className="w-full border rounded-lg p-2 appearance-none"
              onChange={(e) => selectBeneficiary(e.target.value)}
              defaultValue=""
            >
              <option value="">Sélectionner bénéficiaire</option>
              {beneficiaries.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.firstName} {b.lastName} — {b.email || b.phone}
                </option>
              ))}
            </select>
          </div>
        )}

        <Row>
          <Input
            label="Prénom"
            required
            value={formData.beneficiary?.firstName}
            error={showError("beneficiaryFirstName")}
            onChange={(v) =>
              setFormData((p) => ({
                ...p,
                beneficiary: {
                  ...p.beneficiary,
                  firstName: v,
                  fullName:
                    `${v || ""} ${p.beneficiary?.lastName || ""}`.trim(),
                },
              }))
            }
          />
          <Input
            label="Nom"
            required
            value={formData.beneficiary?.lastName}
            error={showError("beneficiaryLastName")}
            onChange={(v) =>
              setFormData((p) => ({
                ...p,
                beneficiary: {
                  ...p.beneficiary,
                  lastName: v,
                  fullName:
                    `${p.beneficiary?.firstName || ""} ${v || ""}`.trim(),
                },
              }))
            }
          />
        </Row>

        {method !== "BANK_TRANSFER" && method !== "INTERAC" && (
          <Input
            label="Email"
            required={method !== "MOBILE_MONEY"}
            value={formData.beneficiary?.email}
            error={showError("beneficiaryEmail")}
            onChange={(v) =>
              setFormData((p) => ({
                ...p,
                beneficiary: { ...p.beneficiary, email: v },
              }))
            }
          />
        )}

        {destIsCA ? (
          <>
            <Input
              label="Adresse (numéro et rue)"
              required
              value={formData.beneficiary?.streetAddress}
              error={showError("beneficiaryStreet")}
              onChange={(v) =>
                setFormData((p) => ({
                  ...p,
                  beneficiary: { ...p.beneficiary, streetAddress: v },
                }))
              }
            />
            <Row>
              <Input
                label="Ville"
                required
                value={formData.beneficiary?.city}
                error={showError("beneficiaryCity")}
                onChange={(v) =>
                  setFormData((p) => ({
                    ...p,
                    beneficiary: { ...p.beneficiary, city: v },
                  }))
                }
              />
              <Input
                label="Province"
                required
                value={formData.beneficiary?.province}
                error={showError("beneficiaryProvince")}
                onChange={(v) =>
                  setFormData((p) => ({
                    ...p,
                    beneficiary: { ...p.beneficiary, province: v },
                  }))
                }
              />
            </Row>
            <Input
              label="Code postal"
              required
              value={formData.beneficiary?.postalCode}
              error={showError("beneficiaryPostal")}
              onChange={(v) =>
                setFormData((p) => ({
                  ...p,
                  beneficiary: { ...p.beneficiary, postalCode: v },
                }))
              }
            />
          </>
        ) : (
          method !== "BANK_TRANSFER" && (
            <Input
              label="Ville"
              required
              value={formData.beneficiary?.city}
              error={showError("beneficiaryCity")}
              onChange={(v) =>
                setFormData((p) => ({
                  ...p,
                  beneficiary: { ...p.beneficiary, city: v },
                }))
              }
            />
          )
        )}

        {method === "MOBILE_MONEY" && (
          <>
            <div className="w-full">
              <label className="text-sm mb-1 block">Opérateur</label>
              <select
                value={formData.beneficiary?.operator || ""}
                onChange={(e) =>
                  setFormData((p) => ({
                    ...p,
                    beneficiary: { ...p.beneficiary, operator: e.target.value },
                  }))
                }
                className="w-full border rounded-lg p-2"
              >
                <option value="">Choisir opérateur</option>
                {operators.map((op) => (
                  <option key={op} value={op}>
                    {op.replaceAll("_", " ")}
                  </option>
                ))}
              </select>
              {showError("beneficiaryOperator") && (
                <p className="text-xs text-red-600">
                  {errors.beneficiaryOperator}
                </p>
              )}
            </div>

            <Input
              label="Téléphone Mobile Money"
              required
              value={formData.beneficiary?.phone}
              error={showError("beneficiaryPhone")}
              onChange={(v) =>
                setFormData((p) => ({
                  ...p,
                  beneficiary: { ...p.beneficiary, phone: v },
                }))
              }
            />
          </>
        )}

        {method === "BANK_TRANSFER" && (
          <Row>
            <Input
              label={
                String(countryTo || "").toUpperCase() === "NG"
                  ? "Bank Name"
                  : "Banque"
              }
              required
              value={formData.beneficiary?.bankName}
              error={showError("beneficiaryBank")}
              onChange={(v) =>
                setFormData((p) => ({
                  ...p,
                  beneficiary: { ...p.beneficiary, bankName: v },
                }))
              }
            />
            <Input
              label={
                String(countryTo || "").toUpperCase() === "NG"
                  ? "Nigerian Account Number"
                  : "Numéro de compte"
              }
              required
              value={formData.beneficiary?.accountNumber}
              error={showError("beneficiaryAccount")}
              onChange={(v) =>
                setFormData((p) => ({
                  ...p,
                  beneficiary: { ...p.beneficiary, accountNumber: v },
                }))
              }
            />
          </Row>
        )}

        {method === "INTERAC" && (
          <Input
            label="Email Interac"
            required
            value={formData.beneficiary?.interacEmail}
            error={showError("beneficiaryInterac")}
            onChange={(v) =>
              setFormData((p) => ({
                ...p,
                beneficiary: { ...p.beneficiary, interacEmail: v },
              }))
            }
          />
        )}

        <div className="flex flex-col sm:flex-row gap-3 pt-4">
          <button
            onClick={onPrev}
            className="w-full sm:w-auto rounded-2xl border border-black/10 dark:border-white/10 bg-white dark:bg-slate-950 py-3 px-4 font-medium text-slate-900 dark:text-white hover:bg-black/5 dark:hover:bg-white/10 transition"
            type="button"
          >
            Retour
          </button>
          <button
            onClick={() => {
              setTouched(true);
              if (canContinue) onNext();
              else window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            className="w-full sm:w-auto rounded-2xl bg-slate-900 text-white py-3 px-4 font-medium hover:opacity-95 transition disabled:opacity-50 disabled:cursor-not-allowed"
            type="button"
          >
            Continuer
          </button>
        </div>
      </Section>
    </div>
  );
}

/* ============================================================
   UI HELPERS
============================================================ */

function Section({ title, children }) {
  return (
    <div className="border rounded-xl p-4 sm:p-5 space-y-4 bg-white dark:bg-slate-900">
      <h3 className="font-semibold text-slate-900 dark:text-white">{title}</h3>
      {children}
    </div>
  );
}

function Row({ children }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>
  );
}

function Input({ label, value, onChange, error, required }) {
  return (
    <div className="flex flex-col space-y-1">
      <label className="text-sm text-slate-700 dark:text-slate-200">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <input
        value={value || ""}
        onChange={(e) => onChange?.(e.target.value)}
        className={`border rounded-lg px-3 py-2 w-full ${
          error ? "border-red-500" : "border-gray-300"
        }`}
      />
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}
